import classNames from "classnames";
import { ChangeEvent, FC, memo, useCallback, useRef, useState } from "react";

import { ReactComponent as DocumentIcon } from "assets/images/common/document.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus-thin.svg";
import { ReactComponent as TrashIcon } from "assets/images/common/trash.svg";

import { CERTIFICATE_ALLOWED_TYPES, CERTIFICATE_MAX_SIZE } from "entities/devices";

import styles from "./style.module.scss";

const CERTIFICATE_ERROR =
  "Unable to upload file. Please make sure the file is in PDF format and does not exceed 10 MB";

type PropTypes = {
  value: File | null;
  onChange: (file: File | null) => void;
  isDisabled?: boolean;
  heading?: string;
};

/** Calibration certificate (PDF) drag-and-drop uploader. */
const CertificateUpload: FC<PropTypes> = ({
  value,
  onChange,
  isDisabled,
  heading = "Calibration certificate",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const handlePick = useCallback(() => {
    if (isDisabled) return;
    inputRef.current?.click();
  }, [isDisabled]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const picked = e.target.files?.[0];
      // Reset so the same file can be re-selected after a failed upload.
      e.target.value = "";
      if (!picked) return;

      const wrongType = !(CERTIFICATE_ALLOWED_TYPES as string[]).includes(picked.type);
      if (wrongType || picked.size > CERTIFICATE_MAX_SIZE) {
        setError(CERTIFICATE_ERROR);
        onChange(null);
        return;
      }
      setError(undefined);
      onChange(picked);
    },
    [onChange],
  );

  const handleRemove = useCallback(() => {
    setError(undefined);
    onChange(null);
  }, [onChange]);

  return (
    <div className={styles.uploader}>
      <p className={styles.heading}>{heading}</p>

      <button
        type="button"
        className={classNames(styles.dropzone, { [styles.dropzoneError]: !!error })}
        onClick={handlePick}
        disabled={isDisabled}
      >
        <span className={styles.dropzoneIcon}>
          <PlusIcon />
        </span>
        <span className={styles.dropzoneText}>
          <span className={styles.dropzoneTitle}>
            Upload the calibration certificate issued by the accredited laboratory
          </span>
          <span className={styles.dropzoneHint}>Accepted format: PDF. Max file size: 10 MB</span>
        </span>
      </button>

      {!!error && <p className={styles.uploadError}>{error}</p>}

      {!!value && !error && (
        <div className={styles.fileChip}>
          <span className={styles.fileIcon}>
            <DocumentIcon />
          </span>
          <span className={styles.fileName}>{value.name}</span>
          <button
            type="button"
            className={styles.fileRemove}
            onClick={handleRemove}
            aria-label="Remove certificate"
          >
            <TrashIcon />
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        onChange={handleChange}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default memo(CertificateUpload);
